import { useState, type ReactNode } from 'react';
import type { AdminMe } from '@trendator/shared';
import { cn } from '../lib/cn';
import { navigate } from '../lib/router';
import { Icons, type IconName } from './icons';
import { Wordmark } from './Wordmark';

interface NavItem {
  path: string;
  label: string;
  icon: IconName;
  /** Раздел появится позже — показываем, но не пускаем. */
  soon?: string;
}

export const NAV: NavItem[] = [
  { path: '/', label: 'Дашборд', icon: 'dashboard' },
  { path: '/trends', label: 'Тренды', icon: 'trends' },
  { path: '/generations', label: 'Генерации', icon: 'generations' },
  { path: '/users', label: 'Юзеры', icon: 'users' },
  { path: '/payments', label: 'Платежи', icon: 'payments' },
  { path: '/providers', label: 'Провайдеры', icon: 'providers' },
  { path: '/queues', label: 'Очереди', icon: 'queues' },
  { path: '/partners', label: 'Партнёрка', icon: 'partners' },
  { path: '/moderation', label: 'Модерация', icon: 'moderation' },
  { path: '/analytics', label: 'Аналитика', icon: 'analytics' },
  { path: '/audit', label: 'Аудит-лог', icon: 'audit' },
];

const MOBILE: IconName[] = ['dashboard', 'trends', 'generations', 'users'];

export const isActive = (item: string, path: string) => (item === '/' ? path === '/' : path === item || path.startsWith(`${item}/`));

/** Ссылка без перезагрузки (обычный клик), с поддержкой Cmd/Ctrl+клик. */
export function Link({ to, className, children, ...p }: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string }) {
  return (
    <a
      {...p}
      href={to}
      className={className}
      onClick={(e) => {
        p.onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

export function Layout({ path, me, onLogout, children }: { path: string; me: AdminMe; onLogout: () => void; children: ReactNode }) {
  const [more, setMore] = useState(false);
  const Ico = (n: IconName) => Icons[n]();
  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh w-[248px] shrink-0 flex-col gap-1 overflow-y-auto border-r border-white/6 bg-side px-4 py-[22px] lg:flex">
        <div className="px-2.5 pt-1 pb-[22px]">
          <Wordmark />
        </div>
        {NAV.map((n) => {
          const on = isActive(n.path, path);
          const cls = cn(
            'flex h-11 items-center gap-3 rounded-[14px] px-3.5 text-[15px] font-medium text-soft',
            on ? 'bg-accent/12 text-white [&_svg]:text-accent' : 'hover:bg-white/5 hover:text-white',
          );
          return n.soon ? (
            <span key={n.path} className={cn(cls, 'cursor-default opacity-45 hover:bg-transparent hover:text-soft')} title={`Появится: ${n.soon}`}>
              {Ico(n.icon)}
              {n.label}
              <span className="ml-auto text-[11px] text-dim">{n.soon}</span>
            </span>
          ) : (
            <Link key={n.path} to={n.path} className={cls} aria-current={on ? 'page' : undefined}>
              {Ico(n.icon)}
              {n.label}
            </Link>
          );
        })}
        <div className="mt-auto flex items-center justify-between gap-2 px-3.5 pt-6 text-sm text-muted">
          <span className="truncate">{me.name}</span>
          <button type="button" onClick={onLogout} className="cursor-pointer text-muted hover:text-white" aria-label="Выйти" title="Выйти">
            {Icons.logout()}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between px-4 pt-4 pb-2 lg:hidden">
          <Wordmark admin={false} />
          <button type="button" onClick={onLogout} className="flex size-11 cursor-pointer items-center justify-center rounded-[14px] border border-white/8 bg-card text-white" aria-label="Выйти">
            {Icons.logout()}
          </button>
        </header>
        <main className="flex min-w-0 flex-1 flex-col gap-5 px-4 pt-2 pb-32 lg:px-7 lg:pt-6 lg:pb-12">{children}</main>
      </div>

      <nav className="fixed inset-x-3 bottom-3.5 z-40 flex rounded-[22px] border border-white/8 bg-card px-1.5 py-1 lg:hidden" aria-label="Разделы">
        {MOBILE.map((icon) => {
          const n = NAV.find((x) => x.icon === icon)!;
          const on = isActive(n.path, path);
          return (
            <Link key={n.path} to={n.path} className={cn('flex flex-1 flex-col items-center gap-1 py-2 text-[11px] font-semibold', on ? 'text-accent' : 'text-muted')}>
              {Ico(icon)}
              {n.label}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          className={cn('flex flex-1 cursor-pointer flex-col items-center gap-1 py-2 text-[11px] font-semibold', more ? 'text-accent' : 'text-muted')}
        >
          {Icons.more()}
          Ещё
        </button>
      </nav>
      {more ? (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMore(false)}>
          <div className="absolute inset-x-3 bottom-24 flex flex-col gap-1 rounded-[22px] border border-white/8 bg-card p-2" onClick={(e) => e.stopPropagation()}>
            {NAV.filter((n) => !MOBILE.includes(n.icon)).map((n) =>
              n.soon ? (
                <span key={n.path} className="flex h-12 items-center gap-3 rounded-[14px] px-3.5 text-[15px] text-dim">
                  {Ico(n.icon)}
                  {n.label}
                  <span className="ml-auto text-xs">{n.soon}</span>
                </span>
              ) : (
                <Link key={n.path} to={n.path} onClick={() => setMore(false)} className="flex h-12 items-center gap-3 rounded-[14px] px-3.5 text-[15px] text-white hover:bg-white/5">
                  {Ico(n.icon)}
                  {n.label}
                </Link>
              ),
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
